import axios from 'axios';
import fileDownload from 'js-file-download';
import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import {
	TEmSalesReportDownloadFileRequest,
	TEmSalesReportDownloadFileResponse,
	TEmSalesReportInvoicingFeeTypesResponse,
	TEmSalesReportProcessFileRequest,
	TEmSalesReportProcessFileResponse,
	TEmSalesReportProcessingTypesResponse,
	TEmSalesReportUploadedFilesRequest,
	TEmSalesReportUploadedFilesResponse,
} from './types';
import {
	getUploadedFilesRequest,
	getUploadedFilesSuccess,
	getUploadedFilesFailure,
	getProcessingTypesRequest,
	getProcessingTypesSuccess,
	getProcessingTypesFailure,
	processFileRequest,
	processFileSuccess,
	processFileFailure,
	downloadFileRequest,
	downloadFileSuccess,
	downloadFileFailure,
	getInvoicingFeeTypesRequest,
	getInvoicingFeeTypesSuccess,
	getInvoicingFeeTypesFailure,
} from './reducer';

function* getUploadedFilesWorker(action: PayloadAction<TEmSalesReportUploadedFilesRequest>) {
	try {
		const response: TEmSalesReportUploadedFilesResponse = yield call(
			api.emSalesReports.getUploadedFiles,
			action.payload,
		);
		yield put(getUploadedFilesSuccess(response));
	} catch (error) {
		yield put(getUploadedFilesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getProcessingTypesWorker() {
	try {
		const response: TEmSalesReportProcessingTypesResponse = yield call(
			api.emSalesReports.getProcessingTypes,
		);
		yield put(getProcessingTypesSuccess(response));
	} catch (error) {
		yield put(getProcessingTypesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* processFileWorker(action: PayloadAction<TEmSalesReportProcessFileRequest>) {
	const { payload } = action;
	try {
		const response: TEmSalesReportProcessFileResponse = yield call(
			api.emSalesReports.processFile,
			payload,
		);
		yield put(processFileSuccess(response));
		notificationContainer('Sales Report processing started', 'success');
		if (payload.reloadBody) {
			yield put(getUploadedFilesRequest(payload.reloadBody));
		}
	} catch (error) {
		const message = axios.isAxiosError(error)
			? String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error')
			: 'Error';
		yield put(processFileFailure(message));
	}
}

function* downloadFileWorker(action: PayloadAction<TEmSalesReportDownloadFileRequest>) {
	const { payload } = action;
	try {
		const response: TEmSalesReportDownloadFileResponse = yield call(
			api.emSalesReports.downloadFile,
			payload.fileId,
		);
		yield put(downloadFileSuccess());
		if (response) {
			fileDownload(response, payload.fileName);
		}
	} catch (error) {
		yield put(downloadFileFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getInvoicingFeeTypesWorker() {
	try {
		const response: TEmSalesReportInvoicingFeeTypesResponse = yield call(
			api.emSalesReports.getInvoicingFeeTypes,
		);
		yield put(getInvoicingFeeTypesSuccess(response));
	} catch (error) {
		yield put(getInvoicingFeeTypesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

export function* emSalesReportsSaga() {
	yield takeLatest(getUploadedFilesRequest.type, getUploadedFilesWorker);
	yield takeLatest(getProcessingTypesRequest.type, getProcessingTypesWorker);
	yield takeLatest(processFileRequest.type, processFileWorker);
	yield takeLatest(downloadFileRequest.type, downloadFileWorker);
	yield takeLatest(getInvoicingFeeTypesRequest.type, getInvoicingFeeTypesWorker);
}
